import { useRef, useState } from "react";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { activateDeal } from "@/api/deals-api";
import {
  websiteAnalyticsApi,
  type WebsiteAnalyticsData,
} from "@/api/website-analytics-api";

/** Details of an org premium-cap rejection, for the "upgrade" copy. */
export interface PremiumCapError {
  currentTracked: number | null;
  limit: number | null;
}

/**
 * Recognise the org premium-cap 403 specifically.
 *
 * Everything else (network, permissions, a missing deal) should surface as an
 * ordinary error toast; only this case gets the upgrade path.
 */
export function parsePremiumCapError(error: unknown): PremiumCapError | null {
  if (!(error instanceof AxiosError)) return null;
  if (error.response?.status !== 403) return null;

  const body = error.response.data as
    | { message?: string; data?: { currentTracked?: number; limit?: number } }
    | undefined;

  if (!/limit exceeded/i.test(body?.message ?? "")) return null;

  return {
    currentTracked: body?.data?.currentTracked ?? null,
    limit: body?.data?.limit ?? null,
  };
}

/** Poll cadence while the first fetch is in flight. */
const POLL_MS = 8000;
/**
 * Stop polling after ~5 minutes. Every deal-detail tab stays mounted, so an
 * uncapped interval would keep hitting the endpoint from a background tab.
 */
const MAX_POLLS = 40;

export const websiteAnalyticsQueryKey = (
  workspaceId: string | null,
  dealId: string
) => ["website-analytics", workspaceId, dealId] as const;

export function useWebsiteAnalytics(dealId: string) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const pollCount = useRef(0);

  return useQuery({
    queryKey: websiteAnalyticsQueryKey(activeWorkspaceId, dealId),
    enabled: !!activeWorkspaceId && !!dealId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async (): Promise<WebsiteAnalyticsData> => {
      const res = await websiteAnalyticsApi.get(activeWorkspaceId as string, dealId);
      // Envelopes ride over HTTP 200 here, so throw to reach isError.
      if (res.status !== "success") {
        throw new Error(res.message || "Failed to load website analytics");
      }
      if (!res.data) throw new Error("Website analytics returned no payload");
      return res.data;
    },
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state !== "not_yet_fetched") {
        pollCount.current = 0;
        return false;
      }
      if (pollCount.current >= MAX_POLLS) return false;
      pollCount.current += 1;
      return POLL_MS;
    },
  });
}

export function useEnableWebsiteAnalytics(dealId: string) {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  const key = websiteAnalyticsQueryKey(activeWorkspaceId, dealId);

  return useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");
      const res = await websiteAnalyticsApi.enable(activeWorkspaceId, dealId);
      if (res.status !== "success") {
        throw new Error(res.message || "Failed to enable website analytics");
      }
      return res;
    },
    onSuccess: (res) => {
      // Flip to "fetching" on this tick so the card never flashes back to the
      // Enable button between the POST resolving and the GET landing - and so
      // the poll starts immediately.
      queryClient.setQueryData<WebsiteAnalyticsData>(key, (prev) => ({
        ...(prev ?? { enabled: true, domain: res.data?.domain ?? null }),
        state: "not_yet_fetched",
        enabled: true,
        domain: res.data?.domain ?? prev?.domain ?? null,
      }));
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Website analytics enabled");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Couldn't enable website analytics"));
    },
  });
}

/**
 * Make the deal premium, then enable analytics on it.
 *
 * Two calls rather than one because premium activation is an existing,
 * quota-bearing concern that other surfaces already own; folding it into the
 * analytics endpoint would duplicate the cap logic in a second place.
 *
 * Hitting the org premium cap is surfaced separately from a generic failure -
 * it is the one error with a useful next step (upgrade or free a slot).
 */
export function useMakePremiumAndEnable(dealId: string) {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { currentDeal, updateDeal } = useCurrentDealStore();
  const fetchDealStats = useDealStatsStore((s) => s.fetchDealStats);
  const [capError, setCapError] = useState<PremiumCapError | null>(null);
  const key = websiteAnalyticsQueryKey(activeWorkspaceId, dealId);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!activeWorkspaceId) throw new Error("No workspace selected");

      await activateDeal(activeWorkspaceId, dealId);

      const res = await websiteAnalyticsApi.enable(activeWorkspaceId, dealId);
      if (res.status !== "success") {
        throw new Error(res.message || "Failed to enable website analytics");
      }
      return res;
    },
    onSuccess: async (res) => {
      setCapError(null);

      // Premium status is global state other surfaces read, so patch the deal
      // store and refresh the quota-backed stats the same way the report
      // activation flow does.
      if (currentDeal) {
        updateDeal({ ...currentDeal, aiTracked: true });
      }
      if (activeWorkspaceId) {
        await fetchDealStats(activeWorkspaceId);
      }

      queryClient.setQueryData<WebsiteAnalyticsData>(key, (prev) => ({
        ...(prev ?? { enabled: true, domain: res.data?.domain ?? null }),
        state: "not_yet_fetched",
        enabled: true,
        is_premium: true,
        domain: res.data?.domain ?? prev?.domain ?? null,
      }));
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Deal marked premium — website analytics enabled");
    },
    onError: (error) => {
      const cap = parsePremiumCapError(error);
      if (cap) {
        // Rendered inline with an upgrade route; a toast would be a dead end.
        setCapError(cap);
        return;
      }
      toast.error(getErrorMessage(error, "Couldn't enable website analytics"));
    },
  });

  return {
    ...mutation,
    capError,
    clearCapError: () => setCapError(null),
  };
}
